import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Spinner from '@johnlewispartnership/wtr-ingredients/ingredients/Spinner';
import Typography from '@johnlewispartnership/wtr-ingredients/foundations/typography';
import { TextLink } from '@johnlewispartnership/wtr-ingredients/ingredients/TextLink';

import { getAlternativesByIngredient } from 'redux/modules/recipes/actions';
import { swapShoppableIngredientById } from 'redux/modules/recipes/actions/swap-shoppable-ingredient';
import { getCustomerSlotBranchId } from 'redux/modules/customer-slot/selectors';

import { trackShoppableSwap } from 'analytics/recipes-tracking';
import type { AlternativeProduct } from 'redux/modules/recipes/actions/get-recipe-alternatives-by-ingredient';
import { getPDPUrl } from '../../../../../utils/product';
import { SwapProductPayload } from './index';
import { SwapProductItem } from './SwapProductItem';
import { isAllergenUpdate, isNewRecipe } from '../ProductGrid/ShoppablePod';
import { CORE } from '../ProductGrid';

import styles from './index.scss';

export interface AlternativeProductsProps {
  pimsId?: string;
  lineNumber?: string;
  closeSwapProduct: () => void;
  recipeId: string;
  groupId: number;
  ingredientId: string;
  setCurrentSwappedProduct: (product: SwapProductPayload) => void;
  youNeedText?: string;
}

interface MappedAlternativeProduct extends SwapProductPayload {
  title: string;
  type: string;
  uom: string;
  amountSelected: number;
  totalPrice: string;
  mainIngredientPimsId: string;
}

export const mapAlternativeProducts = (
  alternativeProduct: AlternativeProduct,
  ingredientId: string,
  pimsId: string,
): MappedAlternativeProduct => {
  return {
    id: alternativeProduct.id,
    name: alternativeProduct.name,
    size: alternativeProduct.weights?.sizeDescription,
    displayPrice: alternativeProduct.pricing?.displayPrice,
    isSponsored: alternativeProduct.isSponsored,
    isNewRecipe: isNewRecipe(alternativeProduct.productTags),
    isAllergenUpdate: isAllergenUpdate(alternativeProduct.productTags),
    productType: alternativeProduct.productType,
    thumbnail: alternativeProduct.images?.small,
    productUrl: getPDPUrl(alternativeProduct.id, alternativeProduct.name) || '',
    lineNumber: alternativeProduct.lineNumber,
    promotions: alternativeProduct.pricing?.promotions ?? [],
    weights: alternativeProduct.weights,
    currentSaleUnitPrice: alternativeProduct.pricing?.currentSaleUnitRetailPrice,
    displayPriceQualifier: alternativeProduct.pricing?.displayPriceQualifier,
    displayUOMPrice: alternativeProduct.pricing?.displayUOMPrice,
    ingredientId,
    title: alternativeProduct.name,
    type: CORE,
    uom: alternativeProduct.weights?.uoms[0],
    amountSelected: 1,
    totalPrice: alternativeProduct.pricing?.displayPrice,
    conflicts: [],
    pricing: {
      displayPriceQualifier: alternativeProduct.pricing?.displayPriceQualifier,
      displayUOMPrice: alternativeProduct.pricing?.displayUOMPrice,
    },
    productTags: alternativeProduct.productTags,
    mainIngredientPimsId: alternativeProduct.mainIngredientPimsId || pimsId,
  };
};

const AlternativeProducts = ({
  pimsId,
  lineNumber,
  closeSwapProduct,
  recipeId,
  groupId,
  ingredientId,
  setCurrentSwappedProduct,
  youNeedText,
}: AlternativeProductsProps) => {
  const branchId = useSelector(getCustomerSlotBranchId) ?? '651';
  const dispatch = useDispatch<WtrDispatch>();
  const [alternativeProducts, setAlternativeProducts] = useState<MappedAlternativeProduct[]>([]);
  const [loadingAlternativeProducts, setLoadingAlternativeProducts] = useState(false);
  const fetchAlternativeProducts = async () => {
    if (!pimsId || !lineNumber) return;
    setLoadingAlternativeProducts(true);
    const products = await dispatch(
      getAlternativesByIngredient({ lineNumber, ingredientId: pimsId, recipeId }),
    );

    if (!products) return;

    setAlternativeProducts(
      products
        .filter(product => product.lineNumber !== lineNumber)
        .map(product => mapAlternativeProducts(product, ingredientId, pimsId)),
    );

    setLoadingAlternativeProducts(false);
  };

  useEffect(
    () => {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      fetchAlternativeProducts();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pimsId, lineNumber],
  );

  const handleSelect = (swappedIngredient: SwapProductPayload) => {
    dispatch(
      swapShoppableIngredientById({
        branchId,
        recipeId,
        groupId,
        ingredientId,
        swappedIngredient,
      }) as unknown as WtrDispatch,
    );

    trackShoppableSwap('swap', recipeId, lineNumber, swappedIngredient?.lineNumber);
    setCurrentSwappedProduct(swappedIngredient);

    closeSwapProduct();
  };

  if (loadingAlternativeProducts) {
    return (
      <div className={styles.contentContainer}>
        <Spinner isActive={loadingAlternativeProducts} />
      </div>
    );
  }

  if (alternativeProducts.length) {
    return (
      <>
        <div className={styles.servingInfo} data-testid="alternative-products-you-need-text">
          {youNeedText}
        </div>
        <div data-testid="alternative-products">
          {alternativeProducts.map(alternativeProduct => (
            <SwapProductItem
              key={alternativeProduct.lineNumber + alternativeProduct.mainIngredientPimsId}
              {...alternativeProduct}
              onSelect={() => handleSelect(alternativeProduct)}
            />
          ))}
        </div>
      </>
    );
  }

  return (
    <div className={styles.contentContainer} data-testid="alternative-products-no-products-found">
      <Typography element="p" styleAs="paragraph" textStyle="body-m" className={styles.message}>
        Sorry we don&apos;t have any alternative items for this ingredient at this time
      </Typography>
      <TextLink onClick={closeSwapProduct} data-testid="return-to-manage-ingredients">
        Return to manage ingredients
      </TextLink>
    </div>
  );
};

export default AlternativeProducts;
